import {
  ParametrizedHotelContext,
  SearchContext
} from '@vio/core/src/analytics/types/Events'

import {PriceWatchBaseToggle} from '../components/PriceWatchBaseToggle'
import {PriceWatchBell} from '../components/PriceWatchBell'
import {PriceWatchLinkButton} from '../components/PriceWatchLinkButton'
import {PriceWatchToggle} from '../components/PriceWatchToggle/PriceWatchToggle'

import type {ContentHotel, Hotel} from '@findhotel/sapi'

export interface InitPriceWatchOptions {
  /** The watch was created without a user action, so callers skip the toast. */
  isAutomatic?: boolean
}

export interface PriceWatchRate {
  taxesIncluded: boolean
  localTaxesIncluded: boolean
  base: number
  hotelFees: number
  taxes: number
}

export interface PriceWatchCTAParams {
  className?: string
  checkIn: string
  checkOut: string
  rooms: string
  roomsNumber: number
  currency: string
  locale: string
  hotel: Hotel | ContentHotel
  rate: PriceWatchRate
  onPriceWatchInitialized?: (options?: InitPriceWatchOptions) => void
  isAuthenticated: boolean
  getSearchContext: (
    state: Record<string, string | number>,
    searchIdParam?: string
  ) => SearchContext
  hotelContext?: ParametrizedHotelContext
  userCountry: string
  roomName?: string
  device: string
  ctaComponent:
    | typeof PriceWatchToggle
    | typeof PriceWatchBell
    | typeof PriceWatchBaseToggle
    | typeof PriceWatchLinkButton
  guests: number
  setWebBottomSheetOpen: () => void
  shouldPulse?: boolean
  hideIcon?: boolean
  alertsManagementUrl?: string
  componentName: string
  trackingContext?: Record<string, unknown>
  room?: PriceWatchRoom
  bookingId?: string
  cancellationPenalties?: string[]
  isFreeCancellation?: boolean
  freeCancellationUntil?: string
  /**
   * The tracking behind the toggle. Pause/resume acts on this exact tracking
   * instead of the entry derived from the room-agnostic itinerary key.
   */
  trackingPriceWatchId?: string
  /**
   * Resolved on/off state of that tracking. Overrides the entry-derived
   * state so paint and click act on the same tracking.
   */
  isTrackingOn?: boolean
  /** Unset means alert framing, which some callers rely on. */
  saveFramingIsOn?: boolean
  /** Holds the toggle's label on alert wording. Nothing else follows it. */
  hasAlertText?: boolean
}

export interface PriceWatchRoom {
  name: string
  roomId: string
  masterId?: string
  boardType?: string
}

/** Room shape returned by the trackings read API — carries `id`, not `roomId`. */
export interface PriceWatchTrackedRoom {
  id?: string
  name: string
  masterId?: string
  boardType?: string
}

export enum PriceWatchFlow {
  IMPLICIT = 'implicit',
  EXPLICIT = 'explicit',
  DEALFREEZE = 'dealfreeze'
}

export enum PriceWatchMetric {
  Created = 'created',
  Changed = 'changed',
  Sent = 'sent',
  Delivered = 'delivered',
  Opened = 'opened',
  Clicked = 'clicked',
  Paused = 'paused',
  Resumed = 'resumed'
}

export interface PriceWatchPayload {
  priceWatchId: string
  userId: string
  hotelId: string
  checkIn: string // ISO date string
  checkOut: string // ISO date string
  rooms: string
  userCountry: string
  device: string
  currency: string
  locale: string
  source: string
  flow: PriceWatchFlow
  timezone: string
  tiers: string[]
  nights: number
  eventId: string
  metric: PriceWatchMetric
  createdAt: string // ISO date string
  rate: PriceWatchRate
  originalRate?: PriceWatchRate
  hotel: {
    name: string
    starRating: number
    city: string
    image: string
  }
  variation: string
  utcOffset: number
  room?: PriceWatchTrackedRoom
  freeCancellationUntil?: string
  bookingId?: string
}

export interface CreatePriceWatch {
  priceWatchId: string
  hotelId: string
  checkIn: string
  checkOut: string
  rooms: string
  userCountry: string
  device: string
  currency: string
  locale: string
  source: string
  flow: PriceWatchFlow
  timezone: string
  rate: PriceWatchRate
  hotel?: {
    name: string
    starRating: string | number
    city?: string
    image: string
  }
  room?: PriceWatchRoom
  tiers?: string[]
  variation?: string
  isFreeCancellation?: boolean
  cancellationPenalties?: string[]
  freeCancellationUntil?: string
  bookingId?: string
  utcOffset?: number
  nights: number
}
